import { BoxProps, useDisclosure } from "@chakra-ui/react";
import { TransactionSortOption } from "@syla/shared/types/requests/GetGroupsRequest";
import React, { useCallback, useEffect, useState } from "react";
import { useLocation } from "react-router-dom";
import { useLocalStorage } from "../../../../hooks/localStorage/useLocalStorage";
import { useElementHeight } from "../../../../hooks/useElementHeight";
import {
  resetGroupList,
  updateGroupList,
} from "../../../../store/actions/groupList";
import { useCurrentAccountStore } from "../../../../store/currentAccountStore";
import { useTransactionsStore } from "../../../../store/transactionsStore";
import { OrderFilterSetting } from "../../../../types/order/orderQuery";
import { PageContainerPx } from "../../../atoms/Containers";
import { TransactionOverview } from "../transactionDrawer/transactionOverview/TransactionOverview";
import { ViewSection } from "./filters/ViewSection";
import { MainTransactionsTable } from "./MainTransactionsTable";
import { TransactionsToolbarSection } from "./TransactionsToolbarSection";
import { useTransactionPagination } from "./useTransactionPagination";

// this state may exist if navigated from another page that
// requires pre-selecting some filter options
export type MainTransactionTableHistoryState = {
  filter?: Partial<OrderFilterSetting>;
};

export const defaultOrderFilterState = {
  transactionType: [],
  ledgerType: [],
  dataSource: [],
  asset: [],
  status: [],
  searchQuery: "",
  date: undefined,
  imports: [],
};

export function TransactionsView({
  pageContainerWidth,
  onAddTransaction,
  totalTransactionCount,
}: {
  pageContainerWidth: BoxProps["width"];
  onAddTransaction: () => void;
  totalTransactionCount?: number;
}): React.ReactElement {
  // wallet id will exist if user navigated from the data source page
  const { state } = useLocation<MainTransactionTableHistoryState | undefined>();

  // maintain a separate state for the drawer, so we can still get smooth close animation
  const { onOpen, onClose, isOpen } = useDisclosure();

  const [selectedTransaction, setSelectedTransaction] = useState<string>();

  const [canDefaultCloseDrawer, setCanDefaultCloseDrawer] = useState(true);

  const [filterSetting, setFilterSetting] = useState<OrderFilterSetting>({
    ...defaultOrderFilterState,
    ...state?.filter,
  });

  const [sortOption, setSortOption] = useLocalStorage<TransactionSortOption>(
    "transactionsSortBy",
    TransactionSortOption.DateAscending,
  );

  // this value will flash to undefined when user is paginating
  const groupListTotal = useTransactionsStore((state) => state.groupListTotal);

  const {
    pageSize,
    setPageSize,
    currentPageIndex,
    setCurrentPageIndex,
    totalItems,
  } = useTransactionPagination(groupListTotal);

  const accountId = useCurrentAccountStore(({ accountId }) => accountId);

  // reload data whenever any settings change
  useEffect(() => {
    if (!accountId) {
      // clean up state on unmount
      return resetGroupList;
    }

    updateGroupList({
      sort: sortOption,
      filter: filterSetting,
      pageParam: currentPageIndex,
      limit: pageSize,
      accountId,
    });
  }, [accountId, currentPageIndex, filterSetting, sortOption, pageSize]);

  const onClickOrder = useCallback(
    (transactionId: string) => {
      // open the detailed view
      setSelectedTransaction(transactionId);
      onOpen();
    },
    [onOpen],
  );

  // The table needs knowledge of the toolbar height, so it can offset the
  // correct amount from the top of the page when 'sticky' is used
  const filterElementHeight = useElementHeight<HTMLDivElement>();

  return (
    <>
      <ViewSection
        w={pageContainerWidth}
        position="sticky"
        left={0}
        px={PageContainerPx}
        pb={4}
        sortOption={sortOption}
        setSortOption={setSortOption}
        pageSize={pageSize}
        setPageSize={setPageSize}
      />

      <TransactionsToolbarSection
        pageContainerWidth={pageContainerWidth}
        filterSetting={filterSetting}
        setFilterSetting={setFilterSetting}
        pageSize={pageSize}
        totalItems={totalItems}
        currentPageIndex={currentPageIndex}
        setCurrentPageIndex={setCurrentPageIndex}
        containerRef={filterElementHeight.ref}
        filters={filterSetting}
        onAddTransaction={onAddTransaction}
      />

      <MainTransactionsTable
        top={`${filterElementHeight.height}px`}
        onClickOrder={onClickOrder}
        filters={filterSetting}
        totalTransactionCount={totalTransactionCount}
      />

      {selectedTransaction && (
        <TransactionOverview
          transactionId={selectedTransaction}
          isOpen={isOpen}
          onClose={onClose}
          onCloseComplete={() => {
            setSelectedTransaction(undefined);
            setCanDefaultCloseDrawer(true);
          }}
          setCanDefaultCloseDrawer={setCanDefaultCloseDrawer}
          canDefaultCloseDrawer={canDefaultCloseDrawer}
        />
      )}
    </>
  );
}
